import { Pagination } from "@/components/ui/Pagination";
import { TableShell, Td, Tr } from "@/components/ui/Table";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { PAGE_SIZE_OPTIONS } from "@/lib/listQuery";

/** Lock the list page to the app chrome; only the table body scrolls. */
export const LIST_PAGE_BODY = "flex h-full min-h-0 flex-col overflow-hidden";
export const LIST_TABLE_SHELL =
  "min-h-0 flex-1 overflow-auto overscroll-contain rounded-b-none [scrollbar-gutter:stable]";

export function EmptyTableRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: React.ReactNode;
}) {
  return (
    <Tr>
      <Td colSpan={colSpan} align="center" className="text-sm text-ink-faint">
        {children}
      </Td>
    </Tr>
  );
}

/**
 * Table + pinned pagination footer. Loading/error occupy the same flex slot
 * so the toolbar does not jump.
 */
export function ListTableFrame({
  isLoading,
  isFetching,
  error,
  errorMessage,
  errorNode,
  onRetry,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  children,
}: {
  isLoading: boolean;
  isFetching?: boolean;
  error?: unknown;
  errorMessage?: string;
  errorNode?: React.ReactNode;
  onRetry?: () => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  children: React.ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="min-h-0 flex-1">
        <LoadingState />
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-3 min-h-0 flex-1">
        {errorNode ?? <ErrorState message={errorMessage ?? ""} onRetry={onRetry} />}
      </div>
    );
  }
  return (
    <div className="mt-3 flex min-h-0 flex-1 flex-col" aria-busy={isFetching}>
      <TableShell className={LIST_TABLE_SHELL}>{children}</TableShell>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disabled={!!isFetching}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        className="shrink-0 rounded-t-none border-t-0"
      />
    </div>
  );
}
